import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import { dropTask } from 'ember-concurrency';
import type FlowModel from 'garaje/models/flow';
import type LocationModel from 'garaje/models/location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type GlobalSettingBatchService from 'garaje/services/global-setting-batch';
import type StateService from 'garaje/services/state';
import { hash } from 'rsvp';

export interface FlowsTaskResponse {
  flows?: FlowModel[];
  locationId?: string;
}

export interface VisitorTypesFlowRouteModel {
  currentLocation: LocationModel;
  flow: FlowModel;
  vrSubscription: StateService['vrSubscription'];
  loadFlowDataTask: VisitorTypesFlowRoute['loadFlowDataTask'];
}

export default class VisitorTypesFlowRoute extends Route {
  @service declare abilities: AbilitiesService;
  @service('global-setting-batch') declare globalSettingBatchService: GlobalSettingBatchService;
  @service declare state: StateService;
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;

  model(params: { flow_id: string }): Promise<VisitorTypesFlowRouteModel> {
    const { visitorsSubscription, currentLocation } = this.state;

    void this.loadFlowDataTask.perform({ locationId: currentLocation.id });

    const include = [
      'sign-in-field-page',
      'agreement-page',
      'agreement-page.agreements',
      'badge',
      'flow-questionnaires',
      'flow-questionnaires.questionnaire',
      'flow-questionnaires.questionnaire.location',
      'photo-page',
      'summary-page',
      'flow-visit-workflow',
      'document-upload-page',
      'document-upload-page.custom-user-document-templates',
    ];
    if (this.abilities.can('configure data privacy settings')) {
      include.push('visitor-document-contacts', 'invite-approval-contacts');
    }

    if (this.featureFlags.isEnabled('visitors-configurable-final-screens')) {
      include.push('summary-page.summary-attributes');
    }

    const flow = this.store.findRecord('flow', params.flow_id, {
      include: include.join(','),
      reload: true,
    });

    return hash({
      currentLocation,
      flow,
      vrSubscription: visitorsSubscription,
      loadFlowDataTask: this.loadFlowDataTask,
    });
  }

  // @ts-ignore
  afterModel(
    { currentLocation, flow }: VisitorTypesFlowRouteModel,
    _transition: Transition,
  ): Transition<unknown> | void {
    if (this.abilities.can('propagate global-setting-batch') && !flow.isGlobalChild) {
      this.globalSettingBatchService.createGlobalSettingBatch({ parent: flow, signInFields: [] });
    }

    // if we change location, we want to make sure that the flow the
    // user is looking at, belongs to "currentLocation".
    if (flow.belongsTo('location').id() !== currentLocation.id) {
      return this.router.transitionTo('visitors.settings.visitor-types.index');
    }
  }

  loadFlowDataTask = dropTask(async (options: { locationId: string }): Promise<FlowsTaskResponse> => {
    const flows = await this.state.loadFlows({ reload: false, locationId: options.locationId });

    return {
      flows,
      locationId: options.locationId,
    };
  });
}
